import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CheckCircle,
  XCircle,
  Play,
  Loader2,
  AlertTriangle,
  Shield,
  MessageSquare,
  FileText,
  Lock,
  Clock,
  ChevronRight,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import QARunDetail from './QARunDetail';
import type {
  QATestSuite,
  QARun,
  QARunResults,
  QATestResult,
  runAutoChecks,
  computeQASummary,
} from '@/types/qaTypes';

interface BotQARunnerProps {
  botId: string;
  tenantId: string;
  botStatus: string | null;
  onStatusChange: (newStatus: string) => void;
}

const categoryIcons: Record<string, React.ReactNode> = {
  refusal: <Shield className="w-4 h-4" />,
  citation: <FileText className="w-4 h-4" />,
  escalation: <MessageSquare className="w-4 h-4" />,
  pii: <Lock className="w-4 h-4" />,
};

const categoryLabels: Record<string, string> = {
  refusal: 'Refusal',
  citation: 'Citation',
  escalation: 'Escalation',
  pii: 'PII',
};

const BotQARunner: React.FC<BotQARunnerProps> = ({
  botId,
  tenantId,
  botStatus,
  onStatusChange,
}) => {
  const { user } = useAdminAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState(0);
  const [totalTests, setTotalTests] = useState(0);
  const [qaRuns, setQaRuns] = useState<QARun[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Suite selection
  const [suites, setSuites] = useState<QATestSuite[]>([]);
  const [selectedSuiteId, setSelectedSuiteId] = useState<string>('');
  const [selectedSuite, setSelectedSuite] = useState<QATestSuite | null>(null);
  
  // Detail view
  const [viewingRun, setViewingRun] = useState<QARun | null>(null);

  useEffect(() => {
    fetchSuitesAndRuns();
  }, [botId]);

  useEffect(() => {
    if (selectedSuiteId && suites.length > 0) {
      const suite = suites.find(s => s.id === selectedSuiteId);
      setSelectedSuite(suite || null);
    }
  }, [selectedSuiteId, suites]);

  const fetchSuitesAndRuns = async () => {
    try {
      // Fetch enabled suites
      const { data: suiteData, error: suiteError } = await supabase
        .from('qa_test_suites')
        .select('*')
        .eq('enabled', true)
        .order('created_at', { ascending: false });

      if (suiteError) throw suiteError;
      
      const parsedSuites = (suiteData || []).map(s => ({
        ...s,
        suite_json: s.suite_json as unknown as QATestSuite['suite_json'],
      })) as QATestSuite[];
      setSuites(parsedSuites);
      
      // Auto-select first suite
      if (parsedSuites.length > 0 && !selectedSuiteId) {
        setSelectedSuiteId(parsedSuites[0].id);
      }

      // Fetch QA runs
      const { data: runData, error: runError } = await supabase
        .from('qa_runs')
        .select('*')
        .eq('bot_id', botId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (runError) throw runError;
      
      const parsedRuns: QARun[] = (runData || []).map(run => ({
        id: run.id,
        bot_id: run.bot_id,
        suite_id: run.suite_id,
        suite_version: run.suite_version,
        tenant_id: run.tenant_id,
        environment: run.environment || 'preview',
        started_at: run.started_at,
        completed_at: run.completed_at,
        operator_id: run.operator_id,
        passed: run.passed ?? false,
        created_at: run.created_at ?? new Date().toISOString(),
        results: run.results as unknown as QARunResults | null,
      }));
      
      setQaRuns(parsedRuns);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAutoChecksLocal = (response: string, checks: { fail_if_regex?: string[]; pass_if_contains_any?: string[] }): boolean | null => {
    if (checks.fail_if_regex && checks.fail_if_regex.length > 0) {
      for (const pattern of checks.fail_if_regex) {
        try {
          if (new RegExp(pattern, 'i').test(response)) {
            return false;
          }
        } catch (e) {
          console.error('Invalid regex pattern:', pattern, e);
        }
      }
    }
    
    if (checks.pass_if_contains_any && checks.pass_if_contains_any.length > 0) {
      const found = checks.pass_if_contains_any.some(
        phrase => response.toLowerCase().includes(phrase.toLowerCase())
      );
      if (found) return true;
    }
    
    return null;
  };

  const runQA = async () => {
    if (!selectedSuite) {
      toast({
        title: 'Select a QA suite first',
        variant: 'destructive',
      });
      return;
    }

    setIsRunning(true);
    setCurrentTest(0);
    
    const tests = selectedSuite.suite_json.tests;
    setTotalTests(tests.length);

    // Create QA run record
    const { data: runData, error: runError } = await supabase
      .from('qa_runs')
      .insert({
        bot_id: botId,
        suite_id: selectedSuite.suite_id,
        suite_version: selectedSuite.suite_version,
        tenant_id: tenantId,
        environment: 'preview',
        started_at: new Date().toISOString(),
        operator_id: user?.id,
        results: { test_results: [], summary: null } as any,
        passed: false,
      })
      .select()
      .single();

    if (runError || !runData) {
      console.error('Error creating QA run:', runError);
      toast({
        title: 'Error starting QA run',
        variant: 'destructive',
      });
      setIsRunning(false);
      return;
    }

    const runId = runData.id;
    const testResults: QATestResult[] = [];

    // Execute each test
    for (let i = 0; i < tests.length; i++) {
      setCurrentTest(i + 1);
      const test = tests[i];
      
      try {
        // Get the user message
        const userMessage = test.conversation.steps.find(s => s.role === 'user')?.content || '';
        
        // Call the advisory-chat edge function
        const { data: chatResponse, error: chatError } = await supabase.functions.invoke('advisory-chat', {
          body: {
            bot_id: botId,
            message: userMessage,
            session_id: `qa-${runId}-${test.test_id}`,
          },
        });

        let responseText = '';
        let citations: any[] = [];
        let confidence = 0;
        let escalated = false;

        if (chatError) {
          responseText = `Error: ${chatError.message}`;
        } else if (chatResponse) {
          responseText = chatResponse.response || chatResponse.answer || '';
          citations = chatResponse.citations || [];
          confidence = chatResponse.confidence || 0;
          escalated = chatResponse.escalated || false;
        }

        // Run auto checks
        const autoResult = runAutoChecksLocal(responseText, test.auto_checks);
        const checksRun: string[] = [];
        if (test.auto_checks.fail_if_regex?.length) checksRun.push('fail_if_regex');
        if (test.auto_checks.pass_if_contains_any?.length) checksRun.push('pass_if_contains_any');

        testResults.push({
          test_id: test.test_id,
          name: test.name,
          category: test.category,
          auto_eval: {
            passed: autoResult,
            checks_run: checksRun,
            response_text: responseText,
            citations,
            confidence,
            escalated,
          },
          operator_review: {
            final_pass: null,
            notes: '',
            reviewed_at: null,
            reviewed_by: null,
          },
        });
      } catch (error) {
        console.error(`Test ${test.test_id} failed:`, error);
        testResults.push({
          test_id: test.test_id,
          name: test.name,
          category: test.category,
          auto_eval: {
            passed: false,
            checks_run: [],
            response_text: `Error: ${String(error)}`,
            citations: [],
            confidence: 0,
            escalated: false,
          },
          operator_review: {
            final_pass: null,
            notes: '',
            reviewed_at: null,
            reviewed_by: null,
          },
        });
      }
    }

    // Compute summary
    const passCriteria = selectedSuite.suite_json.pass_criteria;
    const passedAuto = testResults.filter(r => r.auto_eval.passed === true).length;
    const failedAuto = testResults.filter(r => r.auto_eval.passed === false).length;
    const passRate = tests.length > 0 ? passedAuto / tests.length : 0;
    const hardFailCount = testResults.filter(
      r => r.auto_eval.passed === false && passCriteria.hard_fail_categories.includes(r.category)
    ).length;
    const overallPass = passRate >= passCriteria.min_pass_rate && hardFailCount === 0;

    const finalResults: QARunResults = {
      suite_id: selectedSuite.suite_id,
      suite_version: selectedSuite.suite_version,
      summary: {
        total_tests: tests.length,
        passed_auto: passedAuto,
        failed_auto: failedAuto,
        pass_rate: Math.round(passRate * 100) / 100,
        hard_fail_count: hardFailCount,
        overall_pass: overallPass,
      },
      test_results: testResults,
    };

    // Update QA run with results
    try {
      const { error: updateError } = await supabase
        .from('qa_runs')
        .update({
          results: finalResults as any,
          passed: overallPass,
          completed_at: new Date().toISOString(),
        })
        .eq('id', runId);

      if (updateError) throw updateError;

      // Update bot status
      const newStatus = overallPass ? 'ready_for_review' : 'qa_failed';
      const { error: botError, data: botUpdateData } = await supabase
        .from('bots')
        .update({ status: newStatus })
        .eq('id', botId)
        .select();

      if (botError) {
        console.error('Failed to update bot status:', botError);
        toast({
          title: 'Warning: Status not updated',
          description: 'QA results saved but bot status may not have updated. Try refreshing.',
          variant: 'destructive',
        });
      } else {
        console.log('Bot status updated to:', newStatus, botUpdateData);
        onStatusChange(newStatus);
      }

      toast({
        title: overallPass ? 'QA Passed!' : 'QA Failed',
        description: `${passedAuto}/${tests.length} tests passed. Status updated.`,
        variant: overallPass ? 'default' : 'destructive',
      });

      fetchSuitesAndRuns();
    } catch (error) {
      console.error('Error saving QA run:', error);
      toast({
        title: 'Error saving QA results',
        variant: 'destructive',
      });
    }

    setIsRunning(false);
  };

  const progress = isRunning && totalTests > 0 ? (currentTest / totalTests) * 100 : 0;
  const latestRun = qaRuns[0];

  // If viewing a specific run, show detail view
  if (viewingRun) {
    const passCriteria = selectedSuite?.suite_json.pass_criteria || {
      min_pass_rate: 0.85,
      hard_fail_categories: ['pii', 'refusal'],
    };
    
    return (
      <QARunDetail
        run={viewingRun}
        passCriteria={passCriteria}
        onBack={() => setViewingRun(null)}
        onUpdate={() => {
          fetchSuitesAndRuns();
          setViewingRun(null);
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Run QA Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            QA Test Runner
          </CardTitle>
          <CardDescription>
            Run automated tests to verify bot behavior before publishing
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Suite Selection */}
          <div className="flex items-end gap-4">
            <div className="flex-1 space-y-2">
              <label className="text-sm font-medium">Select QA Suite</label>
              <Select value={selectedSuiteId} onValueChange={setSelectedSuiteId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a suite..." />
                </SelectTrigger>
                <SelectContent>
                  {suites.map(suite => (
                    <SelectItem key={suite.id} value={suite.id}>
                      {suite.suite_id} v{suite.suite_version} ({suite.industry})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={runQA} 
              disabled={isRunning || botStatus === 'live' || !selectedSuite}
              size="lg"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running ({currentTest}/{totalTests})
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run QA Tests
                </>
              )}
            </Button>
          </div>

          {/* Selected Suite Info */}
          {selectedSuite && (
            <div className="flex gap-2 flex-wrap">
              <Badge variant="outline">
                {selectedSuite.suite_json.tests.length} tests
              </Badge>
              <Badge variant="outline">
                Min pass rate: {(selectedSuite.suite_json.pass_criteria.min_pass_rate * 100).toFixed(0)}%
              </Badge>
              <Badge variant="outline">
                Hard fail: {selectedSuite.suite_json.pass_criteria.hard_fail_categories.join(', ')}
              </Badge>
            </div>
          )}

          {/* Progress */}
          {isRunning && (
            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-sm text-muted-foreground">
                Running test {currentTest} of {totalTests}...
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QA Run History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">QA Run History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : qaRuns.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No QA runs yet. Select a suite and click "Run QA Tests" to start.
            </p>
          ) : (
            <div className="space-y-3">
              {qaRuns.map(run => {
                const summary = run.results?.summary;
                
                return (
                  <div 
                    key={run.id}
                    className={`p-4 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors ${
                      run.passed 
                        ? 'bg-green-500/5 border-green-500/20' 
                        : 'bg-destructive/5 border-destructive/20'
                    }`}
                    onClick={() => setViewingRun(run)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {run.passed ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <XCircle className="w-5 h-5 text-destructive" />
                        )}
                        <div>
                          <p className="font-medium text-sm">
                            {run.passed ? 'All Tests Passed' : 'Tests Failed'}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {new Date(run.started_at || run.created_at).toLocaleString()}
                            {run.suite_id && (
                              <Badge variant="outline" className="text-xs">
                                {run.suite_id} v{run.suite_version}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {summary && (
                          <Badge variant={run.passed ? 'default' : 'destructive'}>
                            {summary.passed_auto}/{summary.total_tests} passed
                          </Badge>
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BotQARunner;
